import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PressList, Client } from '@pf/shared';
import {
  usePressLists,
  useCreatePressList,
  useArchivePressList,
  useUnarchivePressList,
  CreatePressListBody,
} from '../hooks/usePressLists';
import { useClients } from '../hooks/useClients';

interface CreateModalProps {
  onClose: () => void;
  onSuccess: (id: string) => void;
  defaultClientId?: string;
}

function CreateModal({ onClose, onSuccess, defaultClientId }: CreateModalProps) {
  const createList = useCreatePressList();
  const { data: clientsData } = useClients();
  const clients = (clientsData as unknown as { clients: Client[] } | undefined)?.clients ?? [];
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState(defaultClientId ?? '');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const body: CreatePressListBody = { name };
      if (description.trim()) body.description = description.trim();
      if (clientId) body.clientId = clientId;
      const result = await createList.mutateAsync(body);
      onSuccess((result as unknown as { pressList: PressList }).pressList?.id ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create list');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Press List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Tech Media Q2 2026"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Optional description..."
            />
          </div>
          {clients.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client
              </label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">No client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createList.isPending}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
            >
              {createList.isPending ? 'Creating...' : 'Create list'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface RowActionsProps {
  list: PressList;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  busy: boolean;
}

function RowActions({ list, onArchive, onUnarchive, busy }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1 text-gray-400 hover:text-gray-600 rounded"
        title="Actions"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          {list.archivedAt ? (
            <button
              onClick={() => {
                onUnarchive(list.id);
                setOpen(false);
              }}
              disabled={busy}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
            >
              Unarchive
            </button>
          ) : (
            <button
              onClick={() => {
                onArchive(list.id);
                setOpen(false);
              }}
              disabled={busy}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
            >
              Archive
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function PressListsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  const urlSearch = searchParams.get('q') ?? '';
  const tagsParam = searchParams.get('tags') ?? '';
  const clientIdParam = searchParams.get('clientId') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [tagInput, setTagInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: clientsData } = useClients();
  const clients = (clientsData as unknown as { clients: Client[] } | undefined)?.clients ?? [];

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch]);

  function addTag(tag: string) {
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed) return;
    const current = tagsParam ? tagsParam.split(',').filter(Boolean) : [];
    if (!current.includes(trimmed)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tags', [...current, trimmed].join(','));
          return next;
        },
        { replace: true },
      );
    }
    setTagInput('');
  }

  function removeTag(tag: string) {
    const current = tagsParam.split(',').filter((t) => t.trim() && t.trim() !== tag);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (current.length > 0) next.set('tags', current.join(','));
        else next.delete('tags');
        return next;
      },
      { replace: true },
    );
  }

  const activeTags = tagsParam ? tagsParam.split(',').filter(Boolean) : [];

  const { data, isLoading, isError } = usePressLists({
    includeArchived: showArchived,
    q: urlSearch || undefined,
    tags: tagsParam || undefined,
    clientId: clientIdParam || undefined,
  });
  const archiveMutation = useArchivePressList();
  const unarchiveMutation = useUnarchivePressList();

  const lists = (data as unknown as { pressLists: PressList[] } | undefined)
    ?.pressLists ?? [];

  function handleCreated(id: string) {
    setShowCreate(false);
    if (id) navigate(`/press-lists/${id}`);
  }

  async function handleArchive(id: string) {
    setActionId(id);
    try {
      await archiveMutation.mutateAsync(id);
    } finally {
      setActionId(null);
    }
  }

  async function handleUnarchive(id: string) {
    setActionId(id);
    try {
      await unarchiveMutation.mutateAsync(id);
    } finally {
      setActionId(null);
    }
  }

  const activeLists = showArchived ? lists : lists.filter((l) => !l.archivedAt);
  const archivedCount = lists.filter((l) => l.archivedAt).length;

  return (
    <div className="p-6">
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onSuccess={handleCreated}
          defaultClientId={clientIdParam || undefined}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Press Lists</h1>
          {activeLists.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              {activeLists.length}{' '}
              {showArchived ? 'total' : 'active'} list
              {activeLists.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {showArchived
                ? 'Hide archived'
                : `Show archived (${archivedCount})`}
            </button>
          )}
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
          >
            New Press List
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        {/* Search and filter bar */}
        <div className="px-4 py-3 border-b border-gray-200 space-y-2">
          <div className="flex flex-wrap gap-2">
            <input
              type="search"
              placeholder="Search by name or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[200px] max-w-sm border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {clients.length > 0 && (
              <select
                value={clientIdParam}
                onChange={(e) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value) next.set('clientId', e.target.value);
                    else next.delete('clientId');
                    next.delete('page');
                    return next;
                  }, { replace: true });
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <input
              type="text"
              placeholder="Add tag filter..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag(tagInput);
                }
              }}
              onBlur={() => {
                if (tagInput.trim()) addTag(tagInput);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-36"
            />
          </div>
          {(activeTags.length > 0 || clientIdParam) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {clientIdParam && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Client: {clients.find((c) => c.id === clientIdParam)?.name ?? clientIdParam}
                  <button
                    onClick={() => {
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete('clientId');
                        return next;
                      }, { replace: true });
                    }}
                    className="ml-0.5 text-blue-500 hover:text-blue-700"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              )}
              {activeTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  Tag: {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="ml-0.5 text-blue-500 hover:text-blue-700"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </span>
              ))}
              <button
                onClick={() => {
                  setSearchParams({}, { replace: true });
                  setSearchInput('');
                  setTagInput('');
                }}
                className="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            Loading...
          </div>
        )}
        {isError && (
          <div className="px-6 py-10 text-center text-sm text-red-600">
            Failed to load press lists.
          </div>
        )}
        {!isLoading && !isError && activeLists.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            {urlSearch || tagsParam || clientIdParam
              ? 'No press lists match your search or filters.'
              : 'No press lists yet. Create your first one.'}
          </div>
        )}
        {!isLoading && activeLists.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Description', 'Contacts', 'Created', ''].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {activeLists.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => navigate(`/press-lists/${l.id}`)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${l.archivedAt ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {l.name}
                        {l.archivedAt && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                            archived
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {l.description ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {l.contactsCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(l.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowActions
                        list={l}
                        onArchive={handleArchive}
                        onUnarchive={handleUnarchive}
                        busy={actionId === l.id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
